import React, { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { Select, Toast } from "components/commons";
import { DownOutlined } from "@ant-design/icons";
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
// Import fullscreen plugin files from the installed package
import 'leaflet.fullscreen/Control.FullScreen.css';
import 'leaflet.fullscreen/Control.FullScreen.js';

// Ensure `window.L` is available for code that expects global Leaflet
if (typeof window !== 'undefined' && !window.L) {
  window.L = L;
}

const DeviceLocationMap = ({ devices, onLocationChange, venues, onVenueChange, venueDropdownDisabled }) => {
  const mapRef = useRef(null);
  const [map, setMap] = useState(null);
  const [markers, setMarkers] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(null); 
  const [selectedVenue, setSelectedVenue] = useState(null);
  const [mapError, setMapError] = useState(false);
  const [locationChangedByUser, setLocationChangedByUser] = useState(false);
  const [displayFullDetailsByDefault] = useState(false);
  const [isSelectedDeviceValid, setIsSelectedDeviceValid] = useState(false); 


  // 1. Venue Options (Sorted Alphabetically)
  const venueOptions = useMemo(() => {
    if (!Array.isArray(venues)) return [];
    
    const sortedVenues = [...venues].sort((a, b) => {
        const nameA = a.text ? a.text.toUpperCase() : ''; 
        const nameB = b.text ? b.text.toUpperCase() : '';
        
        if (nameA < nameB) return -1;
        if (nameA > nameB) return 1;
        return 0;
    });

    return sortedVenues;
  }, [venues]);


  // 2. Initial Venue Selection (Sets default to the first item in the sorted list)
  useEffect(() => {
    if (venueOptions.length > 0 && selectedVenue === null) {
      setSelectedVenue(venueOptions[0].value); 
    }
  }, [venueOptions, selectedVenue]);
  
  useEffect(() => {
    setLocationChangedByUser(false);
  }, [selectedVenue]);


  // 3. Filtered Devices 
  const filteredDevices = useMemo(() => {
    if (!Array.isArray(devices) || !selectedVenue) return devices; 
    
    const currentVenue = venueOptions.find(v => v.value === selectedVenue);
    
    if (currentVenue && currentVenue.text) {
        const venueNameFilter = currentVenue.text.trim();
        
        const result = devices.filter(device => 
            device.venueName && device.venueName.trim() === venueNameFilter
        );
        
        return result;
    }
    return devices;
  }, [devices, selectedVenue, venueOptions]);


  // 4. Location Options (Uses filteredDevices)
  const locationOptions = useMemo(() => {
    const options = [
      { text: "All Devices", value: null },
      ...filteredDevices.map((device, index) => ({
        text: `${device.deviceName || device.deviceImei || device.model || device.venueName || `Device ${index + 1}`}`,
        value: index,
        device: device
      }))
    ];
    return options.sort((a, b) => {
      if (a.text === "All Devices") return -1;
      if (b.text === "All Devices") return 1;
      return a.text.localeCompare(b.text);
    });
  }, [filteredDevices]);

  // 5. Calculate valid pins count (devices with valid coordinates)
  const validPinsCount = useMemo(() => {
    return filteredDevices.filter(d => {
      const lat = typeof d.latitude === "number" ? d.latitude : parseFloat(d.latitude);
      const lng = typeof d.longitude === "number" ? d.longitude : parseFloat(d.longitude);
      return typeof lat === "number" && typeof lng === "number" &&
             !isNaN(lat) && !isNaN(lng) &&
             lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180;
    }).length;
  }, [filteredDevices]);


  // --- Handlers ---

  const handleLocationChange = (name, { value }) => {
    setSelectedLocation(value);
    setLocationChangedByUser(true);
    if (onLocationChange) {
      onLocationChange(value !== null ? filteredDevices[value] : null);
    }
  };

  const handleVenueChange = (name, { value }) => {
    setSelectedVenue(value);
    setSelectedLocation(null);
    setLocationChangedByUser(false);
    
    if (onVenueChange) {
      onVenueChange(value); 
    }
  };


  // --- Map and Marker Logic ---

  // Load Leaflet fullscreen plugin
  const loadFullscreenPlugin = useCallback((mapInstance) => {
    if (!mapInstance || !window.L) return;

    try {
      // Prefer the helper factory if available: L.control.fullscreen()
      if (window.L.control && typeof window.L.control.fullscreen === 'function') {
        const ctrl = window.L.control.fullscreen({ position: 'topright' });
        mapInstance.addControl(ctrl);
        return;
      }

      // Fallback to constructor name used by plugin (Control.FullScreen)
      if (window.L.Control && window.L.Control.FullScreen) {
        mapInstance.addControl(new window.L.Control.FullScreen({ position: 'topright' }));
        return;
      }

      console.warn('Leaflet fullscreen control not found on window.L');
    } catch (err) {
      console.error('Error adding fullscreen control:', err);
    }
  }, []);

  const initializeMap = useCallback(() => {
    if (mapRef.current && window.L) {
      try {
        const mapInstance = window.L.map(mapRef.current).setView([28.6139, 77.2090], 10);
        // Prevent tiles from wrapping horizontally so the world is shown only once
        window.L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
          attribution: '© OpenStreetMap contributors',
          maxZoom: 19,
          noWrap: true,
        }).addTo(mapInstance);
        // Constrain panning to the world bounds to avoid duplicated world copies
        try {
          mapInstance.setMaxBounds([[-90, -180], [90, 180]]);
        } catch (e) {
          // ignore if the map implementation doesn't support setMaxBounds
        }
        setMap(mapInstance);
        loadFullscreenPlugin(mapInstance);
      } catch (error) {
        console.error("Leaflet map initialization failed:", error);
        setMapError(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapRef, loadFullscreenPlugin]);

  useEffect(() => {
    initializeMap();
    
    return () => {
      if (map) {
        map.remove();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initializeMap]);
  
  // RESTORED: Full helper functions for popup content
  const getHelperFunctions = useCallback(() => {
    const formatDateTime = (dt) => {
      if (!dt || typeof dt !== 'object') return '-';
      const date = dt.date || '';
      const time = dt.time || '';
      return `${date}${date && time ? ', ' : ''}${time}`;
    };

    const generateFullPopupContent = (device, lat, lng, id) => `
      <div style="padding: 5px; min-width: 200px;">
        <h4 style="margin: 0 0 8px 0; color: #333; font-size: 14px; font-weight: bold;">${device.deviceName || id} (Full)</h4>
        <p style="margin: 2px 0; color: #666; font-size: 12px;"><strong>IMEI:</strong> ${device.deviceImei || '-'}</p>
        <p style="margin: 2px 0; color: #666; font-size: 12px;"><strong>User:</strong> ${device.userDetails || '-'}</p>
        <p style="margin: 2px 0; color: #666; font-size: 12px;"><strong>Model:</strong> ${device.model || '-'}</p>
        <p style="margin: 2px 0; color: #666; font-size: 12px;"><strong>Venue:</strong> ${device.venueName || '-'}</p>
        <p style="margin: 2px 0; color: #666; font-size: 12px;"><strong>OS Version:</strong> ${device.osVersion || '-'}</p>
        <p style="margin: 2px 0; color: #666; font-size: 12px;"><strong>Firmware:</strong> ${device.firmwareVersion || '-'}</p>
        <p style="margin: 2px 0; color: #666; font-size: 12px;"><strong>Software:</strong> ${device.softwareVersion || '-'}</p>
        <p style="margin: 2px 0; color: #666; font-size: 12px;"><strong>Registered:</strong> ${formatDateTime(device.deviceRegisterDateTime)}</p>
        <p style="margin: 2px 0; color: #666; font-size: 12px;"><strong>Last Location:</strong> ${formatDateTime(device.deviceLastLocationDateTime)}</p>
        <p style="margin: 4px 0 0 0; color: #888; font-size: 11px;"><strong>Coordinates:</strong> ${lat?.toFixed(4)}, ${lng?.toFixed(4)}</p>
        <button 
          id="basic-summary-btn-${id}" 
          style="display: block; margin-top: 10px; padding: 5px 8px; font-size: 11px; cursor: pointer; color: #007bff; background-color: #e6f7ff; border: 1px solid #91d5ff; border-radius: 4px; width: 100%; text-align: center;"
        >
          &lt; View Basic Summary
        </button>
      </div>
    `;

    const generateBasicPopupContent = (device, id) => `
      <div style="padding: 5px; min-width: 150px;">
        <h4 style="margin: 0 0 8px 0; color: #333; font-size: 14px; font-weight: bold;">${device.deviceName || id}</h4>
        <p style="margin: 2px 0; color: #666; font-size: 12px;"><strong>IMEI:</strong> ${device.deviceImei || '-'}</p>
        <p style="margin: 2px 0; color: #666; font-size: 12px;"><strong>Venue:</strong> ${device.venueName || '-'}</p>
        <p style="margin: 2px 0; color: #666; font-size: 12px;"><strong>Last Seen:</strong> ${formatDateTime(device.deviceLastLocationDateTime)}</p>
        <button 
          id="full-details-btn-${id}" 
          style="display: block; margin-top: 10px; padding: 5px 8px; font-size: 11px; cursor: pointer; color: white; background-color: #007bff; border: none; border-radius: 4px; width: 100%; text-align: center;"
        >
          View Full Details &gt;
        </button>
      </div>
    `;

    const setupPopupContentHandler = (marker, device, lat, lng, id) => {
        const initialContent = displayFullDetailsByDefault 
            ? generateFullPopupContent(device, lat, lng, id) 
            : generateBasicPopupContent(device, id);
        
        marker.setPopupContent(initialContent); 

        // Handlers to swap between basic and full view
        const handleFullClick = (e) => {
            if (e.target.id === `full-details-btn-${id}`) {
                e.preventDefault();
                e.stopPropagation();
                marker.setPopupContent(generateFullPopupContent(device, lat, lng, id));
                // Re-attach event listeners after content change
                setTimeout(() => {
                    const basicBtn = marker.getPopup()._container.querySelector(`#basic-summary-btn-${id}`);
                    if (basicBtn) basicBtn.onclick = handleBasicClick;
                }, 0);
            }
        };

        const handleBasicClick = (e) => {
            if (e.target.id === `basic-summary-btn-${id}`) {
                e.preventDefault();
                e.stopPropagation();
                marker.setPopupContent(generateBasicPopupContent(device, id));
                // Re-attach event listeners after content change
                setTimeout(() => {
                    const fullBtn = marker.getPopup()._container.querySelector(`#full-details-btn-${id}`);
                    if (fullBtn) fullBtn.onclick = handleFullClick;
                }, 0);
            }
        };
        
        // Reattach event listeners every time the popup opens
        marker.on('popupopen', () => {
            const fullBtn = marker.getPopup()._container.querySelector(`#full-details-btn-${id}`);
            const basicBtn = marker.getPopup()._container.querySelector(`#basic-summary-btn-${id}`);

            if (fullBtn) { fullBtn.onclick = handleFullClick; }
            if (basicBtn) { basicBtn.onclick = handleBasicClick; }
        });
    };

    return { 
        isValidCoord: (lat, lng) => (
            typeof lat === "number" && typeof lng === "number" &&
            !isNaN(lat) && !isNaN(lng) &&
            lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180
        ),
        getCustomIcon: (L) => L.divIcon({
            html: `<div style='background-color:#ff5722;width:20px;height:20px;border-radius:50% 50% 50% 0;border:2px solid white;transform:rotate(-45deg);box-shadow:0 2px 4px rgba(0,0,0,0.3);position:relative;'><div style='width:8px;height:8px;background-color:white;border-radius:50%;position:absolute;top:4px;left:4px;transform:rotate(45deg);'></div></div>`,
            className: 'custom-marker',
            iconSize: [20, 20],
            iconAnchor: [10, 20],
            popupAnchor: [0, -20]
        }),
        setupPopupContentHandler
    };
  }, [displayFullDetailsByDefault]); 


  // --- Marker and Map View Control ---
  useEffect(() => {
    if (!map || !window.L) return;
    markers.forEach(marker => map.removeLayer(marker));
    
    const { isValidCoord, getCustomIcon, setupPopupContentHandler } = getHelperFunctions();

    if (selectedLocation === null) {
      const validDevices = filteredDevices.map((device, index) => {
        const lat = typeof device.latitude === "number" ? device.latitude : parseFloat(device.latitude);
        const lng = typeof device.longitude === "number" ? device.longitude : parseFloat(device.longitude);
        
        if (!isValidCoord(lat, lng)) return null; 
        
        return { device, lat, lng, index };
      }).filter(Boolean);

      if (validDevices.length === 0 && locationChangedByUser) {
        // Warning is commented out, Toast is not used here for multi-marker view
      }
      
      setMarkers([]);
      if (validDevices.length === 0) return;
      
      const customIcon = getCustomIcon(window.L);
      const newMarkers = validDevices.map(({ device, lat, lng, index }) => {
        const marker = window.L.marker([lat, lng], { icon: customIcon }).addTo(map);
        marker.bindPopup('', { closeButton: true, autoClose: false, closeOnClick: false, autoPan: false }); 
        setupPopupContentHandler(marker, device, lat, lng, index);
        return marker;
      });

      setMarkers(newMarkers);
      
      if (newMarkers.length > 0) {
        const group = new window.L.featureGroup(newMarkers);
        map.fitBounds(group.getBounds().pad(0.1));
        if (newMarkers.length === 1) {
          map.setZoom(Math.max(map.getZoom(), 15));
        }
        //newMarkers.forEach(marker => marker.openPopup());
      }
    } else {
      // VIEW SINGLE SELECTED DEVICE
      const device = filteredDevices[selectedLocation];
      const lat = typeof device?.latitude === "number" ? device?.latitude : parseFloat(device?.latitude);
      const lng = typeof device?.longitude === "number" ? device?.longitude : parseFloat(device?.longitude);

      if (!isValidCoord(lat, lng)) {
        Toast({
            content: 'No valid map coordinates found for the selected device.',
            error: true,
            icon: "exclamation-fill",
        }).open();
        setMarkers([]);
        setIsSelectedDeviceValid(false);
        return;
      }

      const customIcon = getCustomIcon(window.L);
      const marker = window.L.marker([lat, lng], { icon: customIcon }).addTo(map);
      
      marker.bindPopup('', { closeButton: true, autoClose: false, closeOnClick: false }); 
      setupPopupContentHandler(marker, device, lat, lng, selectedLocation);
      
      setMarkers([marker]);
      setIsSelectedDeviceValid(true);
      map.setView([lat, lng], 15);
      marker.openPopup(); 
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, filteredDevices, onLocationChange, selectedLocation, getHelperFunctions, locationChangedByUser]); 


  // --- Render ---

  return (
    <div className="flex flex-col gap-6 w-full">

      {/* Filter Container */}
      <div className="flex gap-4 items-end bg-white border rounded-xl shadow p-4 w-full">

        <div className="flex flex-col">
          <label className="text-xs text-gray-500 mb-1">Select Venue</label>
          <Select
            className="w-96"
            suffixIcon={<DownOutlined style={{ marginLeft: "8px" }} />}
            name="selectedVenue"
            options={venueOptions}
            value={selectedVenue}
            onChange={handleVenueChange}
            placeholder="Select Venue"
            disabled={venueDropdownDisabled}
          />
        </div>

        <div className="flex flex-col">
          <label className="text-xs text-gray-500 mb-1">Device</label>
          <Select
            className="w-96"
            suffixIcon={<DownOutlined style={{ marginLeft: "8px" }} />}
            name="selectedLocation"
            options={locationOptions}
            value={selectedLocation}
            onChange={handleLocationChange}
            placeholder="Select Device Location"
            emptyPlaceHolder="No devices available"
            sortOptions={false}
          />
        </div>
        
        {/* Count Display */}
        <div className="ml-auto text-xs text-gray-600 bg-gray-100 px-3 py-2 rounded-lg border">
          Showing:{" "}
          <span className="text-blue-600 font-semibold">
            {selectedLocation !== null ? (isSelectedDeviceValid ? 1 : 0) : validPinsCount}
          </span>{" "}
          / {filteredDevices.length}
        </div>

      </div>

      {/* Map Container */}
      <div>
        {mapError ? (
          <div className="w-full h-96 border border-gray-300 rounded-lg flex items-center justify-center bg-gray-50">
            <div className="text-center p-6">
              <div className="text-red-500 mb-2">
                <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 15.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Map Loading Error</h3>
              <p className="text-sm text-gray-600">
                Unable to load the map. Please check your internet connection and try again.
              </p>
            </div>
          </div>
        ) : (
          <div
            ref={mapRef}
            className="w-full h-96 border border-gray-300 rounded-lg"
            style={{ minHeight: '600px', zIndex: 0 }}
          />
        )}
      </div>
    </div>
  );
};

export default DeviceLocationMap;